import { useEffect } from 'react'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { PageTitle } from '~/components/ui/PageTitle'

/** sessionStorage flag: set once we've reloaded for a stale chunk. */
export const RELOAD_KEY = 'chunk-reload'

/**
 * A deploy replaced the JS chunks this tab was built against (the old chunk
 * 404s and the CDN answers with HTML). Reload once to pick up the new build.
 */
export function isStaleChunkError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|is not a valid JavaScript MIME type/i.test(
    message,
  )
}

export function reloadForStaleChunk() {
  if (sessionStorage.getItem(RELOAD_KEY)) return false
  sessionStorage.setItem(RELOAD_KEY, '1')
  window.location.reload()
  return true
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const stale = isStaleChunkError(error)

  useEffect(() => {
    if (stale) reloadForStaleChunk()
  }, [stale])

  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <PageTitle size="md">
        {stale ? 'Updating…' : 'Something went wrong'}
      </PageTitle>
      <p className="mt-4 text-fg-muted">
        {stale
          ? 'A new version of the site is available. Reloading…'
          : 'Sorry — this page failed to load.'}
      </p>
      {!stale && (
        <button
          type="button"
          onClick={() => {
            reset()
            window.location.reload()
          }}
          className="mt-8 cursor-pointer font-bold text-accent underline hover:text-accent-hover"
        >
          Try again
        </button>
      )}
    </div>
  )
}
